import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Divider, Typography, Stack, MenuItem } from '@mui/material';
// routes
import { PATH_DASHBOARD } from 'src/routes/paths';
// auth
import { useAuth0 } from '@auth0/auth0-react';
// components
import { CustomAvatar } from 'src/components/custom-avatar';
import { toast } from 'sonner';
import MenuPopover from 'src/components/menu-popover';
import { IconButtonAnimate } from 'src/components/animate';
import { useUser } from 'src/hooks/query/userHooks';
import { getPaletteColorByName } from 'src/components/custom-avatar/utils';
import { useCreatePasswordChangeTicket } from 'src/hooks/query/emailVerificationHooks';
import { resetMixpanel } from 'src/utils/analytics';

// ----------------------------------------------------------------------

const OPTIONS = [
  {
    label: 'Home',
    linkTo: '/',
  },
  {
    label: 'Settings',
    linkTo: PATH_DASHBOARD.settings.root,
  },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const navigate = useNavigate();
  const { mutate: createPasswordChangeTicket } = useCreatePasswordChangeTicket();
  const { user: auth0User } = useAuth0();

  const { user, logout } = useAuth0();
  const { data: currentUser } = useUser();

  const [openPopover, setOpenPopover] = useState<HTMLElement | null>(null);
  const [showChangePassword, setShowChangePassword] = useState<boolean>(false);

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const handleLogout = async () => {
    try {
      logout({ logoutParams: { returnTo: window.location.origin } });
      resetMixpanel();
      handleClosePopover();
      localStorage.removeItem('lastUserId');
    } catch (error) {
      console.error(error);
      toast.error('Unable to logout!');
    }
  };

  const handleClickItem = (path: string) => {
    handleClosePopover();
    navigate(path);
  };

  const theme = useTheme();
  const namePalette = getPaletteColorByName(currentUser?.first_name || user?.displayName, theme);

  useEffect(() => {
    if (auth0User?.sub && auth0User.sub.startsWith('auth0|')) {
      setShowChangePassword(true);
    }
  }, [auth0User?.sub]);

  return (
    <>
      <IconButtonAnimate
        size="small"
        onClick={handleOpenPopover}
        sx={{
          p: 1,
          width: 40,
          height: 40,
          backgroundColor: (theme) => alpha(theme.palette.grey[900], 0.08),
          border: 2,
          borderColor: openPopover ? namePalette.main : 'transparent',
        }}
      >
        <CustomAvatar
          sx={{
            width: 36,
            height: 36,
            border: 2,
            borderColor: (theme) => theme.palette.background.paper,
          }}
          src={currentUser?.signed_url}
          alt={currentUser?.first_name || user?.displayName}
          name={currentUser?.first_name || user?.displayName}
        />
      </IconButtonAnimate>

      <MenuPopover open={openPopover} onClose={handleClosePopover} sx={{ width: 200, p: 0 }}>
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle2" noWrap sx={{ textTransform: 'capitalize' }}>
            {currentUser?.first_name + ' ' + currentUser?.last_name}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {currentUser?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {OPTIONS.map((option) => (
            <MenuItem key={option.label} onClick={() => handleClickItem(option.linkTo)}>
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        {showChangePassword && (
          <MenuItem
            onClick={() => {
              createPasswordChangeTicket(
                {},
                {
                  onSuccess(data) {
                    window.open(data.ticket, '_blank');
                  },
                }
              );
            }}
            sx={{ m: 1 }}
          >
            Change Password
          </MenuItem>
        )}

        <MenuItem onClick={handleLogout} sx={{ m: 1 }}>
          Log Out
        </MenuItem>
      </MenuPopover>
    </>
  );
}
